import type { CalcResponse, Cell } from '@tabel/shared/schemas/cells';
import type { RecalcHistoryEntry, RecalcNotice, SignedNote } from '@tabel/shared/schemas/rules';
import type { Timesheet } from '@tabel/shared/schemas/timesheets';
import { ruDate } from './format';

/*
 * Пересчёт табелей после публикации или возврата правил (004, US3; contracts/http-api.md, «Изменения маршрутов
 * 001–003»; data-model Б.2 `ruleset_recalc_changes`, Г.5, Г.6; research R11–R13): что сервер добавляет к табелю
 * (`recalcInProgress`, `recalcNotice`), ячейке (`staleEdit`) и расчёту дня (`recalcs`, `signedNotes`), и тексты
 * интерфейса. Часы интерфейс не считает: «было» и «стало» — числа сервера.
 */

/**
 * Плашка пересчёта табеля (`Timesheet.recalcNotice`, data-model Г.6). `moreSince` — с какой даты были ещё пересчёты
 * («Ещё N пересчётов с {дата}», FR-042): в схеме этого поля пока нет, без него строка — без даты.
 */
export type RecalcNoticeView = RecalcNotice & { readonly moreSince?: string };

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (v !== null && typeof v === 'object' ? (v as Rec) : {});
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Идёт пересчёт табеля: «Подписать» недоступна (FR-043). */
export function recalcInProgress(ts: Timesheet | undefined): boolean {
  return ts?.recalcInProgress === true;
}

/** Плашка пересчёта, если сервер её прислал и в ней есть дни. */
export function recalcNoticeOf(ts: Timesheet | undefined): RecalcNoticeView | null {
  const n = ts?.recalcNotice;
  return n && n.days.length > 0 ? n : null;
}

/** Сколько ещё пересчётов и с какой даты. */
export function moreOf(n: RecalcNoticeView): { count: number; since: string | null } | null {
  return n.more > 0 ? { count: n.more, since: n.moreSince ?? null } : null;
}

/**
 * «Правка устарела» ячейки (FR-044, research R13): правка есть, а расчёт под ней изменился. `was` — машинные часы на
 * момент правки (`manualMachineHours`): в схеме ячейки этого поля пока нет — без него текст без чисел.
 */
export function staleEditOf(cell: Cell | null | undefined): { was: number | null } | null {
  if (!cell || cell.manualHours == null || !cell.staleEdit) return null;
  return { was: num(rec(cell).manualMachineHours) };
}

/** Заметки подписанного дня (`signed_notes`, FR-038, FR-049), у которых есть текст: по коду без повторов. */
export function signedNotesOf(calc: CalcResponse | undefined): SignedNote['code'][] {
  const codes = (calc?.signedNotes ?? []).map((n) => n.code).filter((c) => SIGNED_NOTE_TEXT[c] !== undefined);
  return [...new Set(codes)];
}

/** Пересчёты дня для истории дня (`CalcResponse.recalcs`). */
export function recalcsOf(calc: CalcResponse | undefined): readonly RecalcHistoryEntry[] {
  return calc?.recalcs ?? [];
}

// ── Тексты (ASD-STE100; язык проверяет RecalcNotice.test.tsx) ──

const PLURAL = new Intl.PluralRules('ru-RU');
function plural(n: number, [one, few, many]: readonly [string, string, string]): string {
  const c = PLURAL.select(n);
  return c === 'one' ? one : c === 'few' ? few : many;
}
const hoursNum = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

/** «Почему меняем» словами (data-model В.1) — как на странице «Правила расчёта». */
const BASIS_KIND: Readonly<Record<string, string>> = {
  перенос: 'начальные правила',
  ответ_на_вопрос: 'ответ на вопрос реестра',
  решение_руководства: 'решение руководства',
  приказ_распоряжение: 'приказ или распоряжение',
  исправление_ошибки: 'исправление ошибки в правиле',
  изменение_закона: 'изменение закона',
  возврат: 'возврат прежних правил',
};
export const basisKindText = (kind: string | null): string | null =>
  kind ? (BASIS_KIND[kind] ?? null) : null;

/** Состояние дня без часов словами. */
const STATE_WORD: Readonly<Record<string, string>> = {
  нет_выхода: 'нет выхода',
  no_exit: 'нет выхода',
  нет_входа: 'нет входа',
  no_entry: 'нет входа',
  на_объекте: 'на объекте',
  on_site: 'на объекте',
  over_24h: 'больше 24 ч',
  error: 'ошибка расчёта',
  пусто: 'проходов нет',
  empty: 'проходов нет',
};

/** Сторона дня словами: «8 ч», по строкам — «Я 8, Н 2»; без часов — состояние. Часы — те, что прислал сервер. */
export function sideText(side: unknown): string {
  const n = num(side);
  if (n !== null) return `${hoursNum.format(n)} ч`;
  const s = rec(side);
  const cells = Object.entries(rec(s.cells)).filter((e): e is [string, number] => num(e[1]) !== null);
  if (cells.length === 1) return `${hoursNum.format(cells[0]?.[1] ?? 0)} ч`;
  if (cells.length > 1) return cells.map(([row, h]) => `${row} ${hoursNum.format(h)}`).join(', ');
  return STATE_WORD[str(s.state) ?? ''] ?? '—';
}

/** Предложения. */
export const RECALC_TEXT = {
  inProgress: 'Табель пересчитывается по новым правилам. Подпишите, когда пересчёт закончится.',
  staleTitle: 'Расчёт под правкой изменился.',
  staleNote: 'Решите, что оставить в табеле.',
  staleTip: 'Правка устарела: расчёт под правкой изменился.',
  keep: 'Правка оставлена',
  accept: 'Расчёт принят',
  signedAfterPublish: 'После подписи опубликованы новые правила.',
  signedRolledBack: 'День подписан по правилам, которые заменены возвратом.',
} as const;

/** Подписи — до 6 слов. */
export const RECALC_LABEL = {
  region: 'Пересчёт по новым правилам',
  what: 'Что изменилось',
  days: 'Какие дни',
  /** Ссылка на историю правил: «История изменений» — журнал дня в карточке дня (ревью T103, С-29). */
  history: 'История правил',
  dismiss: 'Скрыть сообщение о пересчёте',
  staleEdit: 'правка устарела',
  staleRegion: 'Правка устарела',
  keepEdit: 'Оставить правку',
  acceptCalc: 'Принять расчёт',
} as const;

/** Тексты пометок подписанного дня по коду `signed_notes`. */
export const SIGNED_NOTE_TEXT: Readonly<Record<string, string>> = {
  после_подписи_опубликована_версия: RECALC_TEXT.signedAfterPublish,
  подписан_по_отменённой_версии: RECALC_TEXT.signedRolledBack,
};

/**
 * Тексты с подстановкой. Даты — `YYYY-MM-DD`, на экране — `дд.мм.гггг`. Дата начала версии — всегда «с {дата}»:
 * «правила от {дата}» читалось то датой начала, то датой публикации (ревью T103, В-5).
 */
export const sayRecalc = {
  /** «Часы пересчитаны по правилам, которые действуют с 01.10.2026: изменились 2 дня у 2 человек.» */
  notice: (validFrom: string, days: number, people: number) =>
    `Часы пересчитаны по правилам, которые действуют с ${ruDate(validFrom)}: ${plural(days, ['изменился', 'изменились', 'изменились'])} ${days} ${plural(days, ['день', 'дня', 'дней'])} у ${people} ${plural(people, ['человека', 'человек', 'человек'])}.`,
  /** «Ещё 2 пересчёта с 20.09.2026.» */
  more: (n: number, since?: string | null) =>
    `Ещё ${n} ${plural(n, ['пересчёт', 'пересчёта', 'пересчётов'])}${since ? ` с ${ruDate(since)}` : ''}.`,
  basisKind: (kind: string) => `Почему меняем: ${basisKindText(kind) ?? 'другое основание'}`,
  decided: (text: string) => `Что решили: ${text}`,
  dayLine: (name: string, date: string, before: string, after: string) =>
    `${name}, ${ruDate(date)}: ${before} → ${after}`,
  /** «Расчёт под правкой изменился: было 9 ч, стало 8 ч.» */
  staleEdit: (was: string | null, now: string | null) =>
    was !== null && now !== null
      ? `Расчёт под правкой изменился: было ${was} ч, стало ${now} ч.`
      : RECALC_TEXT.staleTitle,
  /** «Пересчитано по правилам с 01.10.2026 (решение руководства): было 9 ч → стало 9,25 ч» */
  historyLine: (validFrom: string | null, kind: string | null, before: string, after: string) => {
    const why = basisKindText(kind);
    const from = validFrom ? ` с ${ruDate(validFrom)}` : '';
    return `Пересчитано по правилам${from}${why ? ` (${why})` : ''}: было ${before} → стало ${after}`;
  },
};
